// FormazioneVotingScreen.tsx - Vote for next match lineup with pitch view

import React, {useState, useEffect, useCallback} from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Image,
  Alert,
  Modal,
  Dimensions,
  TextInput,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useNavigation} from '@react-navigation/native';
import {NativeStackNavigationProp} from '@react-navigation/native-stack';
import {useTheme} from '../hooks/useTheme';
import {Header} from '../components/Header';
import {Icon} from '../components/Icon';
import {API_CONFIG} from '../config/api';
import AsyncStorage from '@react-native-async-storage/async-storage';

const {width: SCREEN_WIDTH} = Dimensions.get('window');
const PITCH_WIDTH = SCREEN_WIDTH - 32;
const PITCH_HEIGHT = PITCH_WIDTH * 1.4;

// Session ID helper
const getSessionId = async (): Promise<string> => {
  const key = '@laviola_session_id';
  let sessionId = await AsyncStorage.getItem(key);
  if (!sessionId) {
    sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
    await AsyncStorage.setItem(key, sessionId);
  }
  return sessionId;
};

// Types
interface Player {
  id: number;
  name: string;
  jersey_number: number;
  position: string;
  specific_position: string;
  role: string;
  image: string | null;
  unavailable?: boolean;
  unavailable_reason?: string;
}

interface MatchInfo {
  match_id: string;
  home_team: {name: string; logo: string | null};
  away_team: {name: string; logo: string | null};
  match_date: string;
  status: string;
}

interface VotingData {
  match: MatchInfo | null;
  players_by_role: {[key: string]: Player[]};
  formations: string[];
  already_voted: boolean;
  message?: string;
}

type RootStackParamList = {
  FormazioneRisultati: {matchId: string};
};

// Formation schemas - matches web blade exactly
// Each slot has a specific position code for player filtering
const FORMATION_SCHEMAS: {[key: string]: string[][]} = {
  '4-3-3': [['LB', 'CB', 'CB', 'RB'], ['CM', 'CM', 'CM'], ['LW', 'ST', 'RW']],
  '4-4-2': [['LB', 'CB', 'CB', 'RB'], ['LM', 'CM', 'CM', 'RM'], ['ST', 'ST']],
  '3-5-2': [['CB', 'CB', 'CB'], ['LM', 'CM', 'CDM', 'CM', 'RM'], ['ST', 'ST']],
  '3-4-3': [['CB', 'CB', 'CB'], ['LM', 'CM', 'CM', 'RM'], ['LW', 'ST', 'RW']],
  '3-4-1-2': [['CB', 'CB', 'CB'], ['LM', 'CM', 'CM', 'RM'], ['CAM'], ['ST', 'ST']],
  '3-4-2-1': [['CB', 'CB', 'CB'], ['LM', 'CM', 'CM', 'RM'], ['CAM', 'CAM'], ['ST']],
  '4-4-1-1': [['LB', 'CB', 'CB', 'RB'], ['LM', 'CM', 'CM', 'RM'], ['CAM'], ['ST']],
  '4-3-2-1': [['LB', 'CB', 'CB', 'RB'], ['CM', 'CM', 'CM'], ['CAM', 'CAM'], ['ST']],
  '4-3-1-2': [['LB', 'CB', 'CB', 'RB'], ['CM', 'CM', 'CM'], ['CAM'], ['ST', 'ST']],
  '4-2-3-1': [['LB', 'CB', 'CB', 'RB'], ['CDM', 'CDM'], ['LM', 'CAM', 'RM'], ['ST']],
  '4-1-3-2': [['LB', 'CB', 'CB', 'RB'], ['CDM'], ['LM', 'CAM', 'RM'], ['ST', 'ST']],
  '4-1-2-3': [['LB', 'CB', 'CB', 'RB'], ['CDM'], ['CM', 'CM'], ['LW', 'ST', 'RW']],
  '5-3-2': [['LWB', 'CB', 'CB', 'CB', 'RWB'], ['CM', 'CM', 'CM'], ['ST', 'ST']],
  '5-4-1': [['LWB', 'CB', 'CB', 'CB', 'RWB'], ['LM', 'CM', 'CM', 'RM'], ['ST']],
  '4-5-1': [['LB', 'CB', 'CB', 'RB'], ['LM', 'CM', 'CM', 'CM', 'RM'], ['ST']],
  '4-1-4-1': [['LB', 'CB', 'CB', 'RB'], ['CDM'], ['LM', 'CM', 'CM', 'RM'], ['ST']],
};



// Helper to generate X positions for a row
const getXPositions = (count: number): number[] => {
  if (count === 1) return [50];
  if (count === 2) return [35, 65];
  if (count === 3) return [20, 50, 80];
  if (count === 4) return [12, 37, 63, 88];
  if (count === 5) return [10, 28, 50, 72, 90];
  return [50];
};

// Check if a player can play in a specific slot position
const canPlayPosition = (playerPositions: string[], slotPosition: string): boolean => {
  if (slotPosition === 'GK') {
    return playerPositions.includes('GK');
  }
  
  // Player can't be GK and play outfield
  if (playerPositions.includes('GK') && slotPosition !== 'GK') {
    return false;
  }
  
  // Direct match
  if (playerPositions.includes(slotPosition)) return true;
  
  // Wingback / Fullback interchangeable
  if ((slotPosition === 'RB' || slotPosition === 'RWB') && 
      (playerPositions.includes('RB') || playerPositions.includes('RWB'))) return true;
  if ((slotPosition === 'LB' || slotPosition === 'LWB') && 
      (playerPositions.includes('LB') || playerPositions.includes('LWB'))) return true;
  
  // Central midfielders interchangeable (CM, CDM, CAM)
  const centralMids = ['CM', 'CDM', 'CAM'];
  if (centralMids.includes(slotPosition)) {
    return playerPositions.some(r => centralMids.includes(r));
  }
  
  // Wingers / Side midfielders - match side (L with L, R with R)
  const leftSide = ['LM', 'LW'];
  const rightSide = ['RM', 'RW'];
  if (leftSide.includes(slotPosition)) {
    return playerPositions.some(r => leftSide.includes(r));
  }
  if (rightSide.includes(slotPosition)) {
    return playerPositions.some(r => rightSide.includes(r));
  }
  
  // Attackers interchangeable (ST, CF, SS)
  const attackers = ['ST', 'CF', 'SS'];
  if (attackers.includes(slotPosition)) {
    return playerPositions.some(r => attackers.includes(r));
  }
  
  return false;
};

export const FormazioneVotingScreen: React.FC = () => {
  const {colors, isDark} = useTheme();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<VotingData | null>(null);
  const [selectedFormation, setSelectedFormation] = useState<string>('4-3-3');
  const [selectedPlayers, setSelectedPlayers] = useState<{[slotIndex: number]: Player}>({});
  const [showFormationPicker, setShowFormationPicker] = useState(false);
  const [playerModalVisible, setPlayerModalVisible] = useState(false);
  const [currentSlot, setCurrentSlot] = useState<{index: number; role: string} | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch voting data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const sessionId = await getSessionId();
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/app/formazione/voting`, {
          headers: {'X-Session-Id': sessionId},
        });
        const json = await response.json();
        if (json.success) {
          setData(json);
          if (json.formations?.length > 0) {
            setSelectedFormation(json.formations[0]);
          }
          if (json.already_voted && json.match?.match_id) {
            navigation.replace('FormazioneRisultati', {matchId: json.match.match_id});
          }
        }
      } catch {
        console.error('Error fetching voting data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigation]);

  // Get position layout for current formation - generates slot positions dynamically
  const getPositionLayout = useCallback((): {slotPosition: string; x: number; y: number}[] => {
    const schema = FORMATION_SCHEMAS[selectedFormation] || FORMATION_SCHEMAS['4-3-3'];
    const result: {slotPosition: string; x: number; y: number}[] = [];
    
    // GK always at bottom
    result.push({slotPosition: 'GK', x: 50, y: 90});
    
    // Calculate y-levels dynamically based on row count
    // Space evenly between 72% (defenders) and 15% (forwards)
    const rowCount = schema.length;
    const yStart = 72; // defenders
    const yEnd = 15;   // forwards
    const yStep = (yStart - yEnd) / (rowCount - 1 || 1);
    
    schema.forEach((row, rowIdx) => {
      const y = yStart - (rowIdx * yStep);
      const xPositions = getXPositions(row.length);
      row.forEach((pos, i) => {
        result.push({slotPosition: pos, x: xPositions[i], y});
      });
    });
    
    return result;
  }, [selectedFormation]);

  // Handle slot tap - open player modal
  const handleSlotPress = (index: number, role: string) => {
    setCurrentSlot({index, role});
    setSearchQuery('');
    setPlayerModalVisible(true);
  };

  // Handle player selection from modal
  const handlePlayerSelect = (player: Player) => {
    if (!currentSlot || player.unavailable) return;
    
    // Check if player already selected in another slot
    const existingSlotIndex = Object.entries(selectedPlayers).find(
      ([, p]) => p.id === player.id
    )?.[0];
    
    if (existingSlotIndex !== undefined) {
      // Remove from previous slot
      const newState = {...selectedPlayers};
      delete newState[parseInt(existingSlotIndex, 10)];
      newState[currentSlot.index] = player;
      setSelectedPlayers(newState);
    } else {
      setSelectedPlayers(prev => ({...prev, [currentSlot.index]: player}));
    }
    
    setPlayerModalVisible(false);
    setCurrentSlot(null);
  };

  // Submit vote
  const handleSubmit = async () => {
    const layout = getPositionLayout();
    if (Object.keys(selectedPlayers).length !== layout.length) {
      Alert.alert('Formazione incompleta', `Seleziona tutti i ${layout.length} giocatori per completare la formazione.`);
      return;
    }

    setSubmitting(true);
    try {
      const sessionId = await getSessionId();
      const positions: {[key: string]: number} = {};
      Object.entries(selectedPlayers).forEach(([slotIndex, player]) => {
        positions[`slot-${slotIndex}`] = player.id;
      });
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/app/formazione/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId,
        },
        body: JSON.stringify({
          formation: selectedFormation,
          positions,
        }),
      });
      const json = await response.json();
      
      if (json.success) {
        Alert.alert('Grazie!', json.message, [
          {text: 'Vedi risultati', onPress: () => {
            if (json.match_id) {
              navigation.replace('FormazioneRisultati', {matchId: json.match_id});
            }
          }},
        ]);
      } else {
        if (json.already_voted && json.match_id) {
          Alert.alert('Già votato', json.message, [
            {text: 'Vedi risultati', onPress: () => {
              navigation.replace('FormazioneRisultati', {matchId: json.match_id});
            }},
          ]);
        } else {
          Alert.alert('Errore', json.message || 'Errore durante il voto');
        }
      }
    } catch {
      Alert.alert('Errore', 'Errore di connessione');
    } finally {
      setSubmitting(false);
    }
  };

  // Get available players for modal - filtered by slot position and search
  const getAvailablePlayers = () => {
    if (!currentSlot || !data) return [];
    
    const allPlayers = Object.values(data.players_by_role).flat();
    
    // Filter by position compatibility using specific_position
    const positionFiltered = allPlayers.filter(player => {
      const playerPositions = (player.specific_position || '').split(',').map(p => p.trim().toUpperCase());
      return canPlayPosition(playerPositions, currentSlot.role);
    });
    
    // Filter by search query
    if (!searchQuery) return positionFiltered;
    return positionFiltered.filter(p => 
      p.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Render position slot on pitch
  const renderSlot = (slot: {slotPosition: string; x: number; y: number}, index: number) => {
    const player = selectedPlayers[index];
    const slotSize = 50;
    
    return (
      <TouchableOpacity
        key={index}
        style={[
          styles.pitchSlot,
          {
            left: (slot.x / 100) * PITCH_WIDTH - slotSize / 2,
            top: (slot.y / 100) * PITCH_HEIGHT - slotSize / 2,
            width: slotSize,
            height: slotSize,
          },
        ]}
        onPress={() => handleSlotPress(index, slot.slotPosition)}
        activeOpacity={0.7}
      >
        {player ? (
          <View style={styles.selectedPlayerSlot}>
            <View style={styles.slotImageContainer}>
              {player.image ? (
                <Image source={{uri: player.image}} style={styles.slotPlayerImage} />
              ) : (
                <View style={styles.slotPlayerInitial}>
                  <Text style={styles.slotInitialText}>
                    {player.name.split(' ').pop()?.charAt(0) || '?'}
                  </Text>
                </View>
              )}
              <View style={styles.jerseyBadge}>
                <Text style={styles.jerseyBadgeText}>{player.jersey_number}</Text>
              </View>
            </View>
            <Text style={styles.slotPlayerName} numberOfLines={1}>
              {player.name.split(' ').pop() || player.name}
            </Text>
          </View>
        ) : (
          <View style={styles.emptySlot}>
            <Icon name="plus" size={20} color="rgba(255,255,255,0.9)" />
          </View>
        )}
      </TouchableOpacity>
    );
  };

  // Render player card in modal
  const renderPlayerCard = (player: Player) => {
    const isSelected = Object.values(selectedPlayers).some(p => p.id === player.id);
    const isDisabled = player.unavailable;
    
    return (
      <TouchableOpacity
        key={player.id}
        style={[
          styles.modalPlayerCard,
          isSelected && styles.modalPlayerSelected,
          isDisabled && styles.modalPlayerDisabled,
        ]}
        onPress={() => handlePlayerSelect(player)}
        disabled={isDisabled}
        activeOpacity={0.7}
      >
        {player.image ? (
          <Image 
            source={{uri: player.image}} 
            style={[styles.modalPlayerImage, isDisabled && styles.modalPlayerImageDisabled]} 
          />
        ) : (
          <View style={[styles.modalPlayerInitial, isDisabled && styles.modalPlayerInitialDisabled]}>
            <Text style={styles.modalInitialText}>
              {player.name.split(' ').pop()?.charAt(0) || '?'}
            </Text>
          </View>
        )}
        <Text 
          style={[styles.modalPlayerName, isDisabled && styles.modalPlayerNameDisabled]}
          numberOfLines={2}
        >
          {player.name}
        </Text>
        {isDisabled && player.unavailable_reason && (
          <Text style={styles.unavailableReason}>{player.unavailable_reason}</Text>
        )}
        {isSelected && (
          <View style={styles.selectedBadge}>
            <Icon name="check" size={10} color="#fff" />
          </View>
        )}
      </TouchableOpacity>
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, {backgroundColor: colors.primary}]} edges={[]}>
        <Header 
          showActionBar 
          onBackPress={() => navigation.goBack()} 
          articleUrl="https://laviola.it/prossima-partita"
        />
        <View style={[styles.loadingContainer, {backgroundColor: colors.background}]}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </SafeAreaView>
    );
  }

  if (!data?.match) {
    return (
      <SafeAreaView style={[styles.container, {backgroundColor: colors.primary}]} edges={[]}>
        <Header 
          showActionBar 
          onBackPress={() => navigation.goBack()} 
          articleUrl="https://laviola.it/prossima-partita"
        />
        <View style={[styles.emptyContainer, {backgroundColor: colors.background}]}>
          <Icon name="calendar-days" size={48} color={colors.textSecondary} />
          <Text style={[styles.emptyText, {color: colors.textPrimary}]}>
            {data?.message || 'Nessuna partita disponibile per la votazione'}
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  const layout = getPositionLayout();
  const selectedCount = Object.keys(selectedPlayers).length;

  return (
    <SafeAreaView style={[styles.container, {backgroundColor: colors.primary}]} edges={[]}>
      <Header 
        showActionBar 
        onBackPress={() => navigation.goBack()} 
        articleUrl="https://laviola.it/prossima-partita"
      />
      <ScrollView style={[styles.scrollView, {backgroundColor: colors.background}]} showsVerticalScrollIndicator={false}>
        {/* Match Header - slightly different shade */}
        <View style={styles.matchHeader}>
          <View style={styles.teamRow}>
            {data.match.home_team.logo && (
              <Image source={{uri: data.match.home_team.logo}} style={styles.teamLogo} />
            )}
            <Text style={styles.teamName}>{data.match.home_team.name}</Text>
            <Text style={styles.vsText}>vs</Text>
            {data.match.away_team.logo && (
              <Image source={{uri: data.match.away_team.logo}} style={styles.teamLogo} />
            )}
            <Text style={styles.teamName}>{data.match.away_team.name}</Text>
          </View>
        </View>

        {/* Formation Selector */}
        <View style={styles.formationRow}>
          <Text style={[styles.formationLabel, {color: colors.textSecondary}]}>Scegli il modulo:</Text>
          <TouchableOpacity
            style={[styles.formationDropdown, {backgroundColor: isDark ? colors.surface : '#fff', borderColor: colors.border}]}
            onPress={() => setShowFormationPicker(!showFormationPicker)}
          >
            <Text style={[styles.formationText, {color: colors.textPrimary}]}>{selectedFormation}</Text>
            <Icon name="chevron-down" size={16} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>

        {showFormationPicker && (
          <View style={[styles.formationPicker, {backgroundColor: isDark ? colors.surface : '#fff'}]}>
            {data.formations.map(formation => (
              <TouchableOpacity
                key={formation}
                style={[
                  styles.formationOption,
                  selectedFormation === formation && {backgroundColor: colors.primary + '20'},
                ]}
                onPress={() => {
                  setSelectedFormation(formation);
                  setSelectedPlayers({});
                  setShowFormationPicker(false);
                }}
              >
                <Text style={[
                  styles.formationOptionText,
                  {color: selectedFormation === formation ? colors.primary : colors.textPrimary},
                ]}>
                  {formation}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Football Pitch */}
        <View style={styles.pitchContainer}>
          <View style={styles.pitch}>
            {/* Pitch markings */}
            <View style={styles.pitchInner}>
              {/* Center circle */}
              <View style={styles.centerCircle} />
              <View style={styles.centerLine} />
              {/* Penalty areas */}
              <View style={styles.penaltyAreaTop} />
              <View style={styles.penaltyAreaBottom} />
              <View style={styles.goalAreaTop} />
              <View style={styles.goalAreaBottom} />
            </View>
            
            {/* Position slots */}
            {layout.map((slot, index) => renderSlot(slot, index))}
          </View>
        </View>

        {/* Submit Button */}
        <TouchableOpacity
          style={[
            styles.submitButton,
            {backgroundColor: selectedCount === layout.length ? colors.primary : colors.textSecondary},
          ]}
          onPress={handleSubmit}
          disabled={submitting || selectedCount !== layout.length}
        >
          {submitting ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.submitText}>
              Invia formazione ({selectedCount}/{layout.length})
            </Text>
          )}
        </TouchableOpacity>

        <View style={styles.bottomPadding} />
      </ScrollView>

      {/* Player Selection Modal */}
      <Modal
        visible={playerModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setPlayerModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, {backgroundColor: isDark ? colors.surface : '#fff'}]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, {color: colors.textPrimary}]}>Scegli Giocatore</Text>
              <TouchableOpacity onPress={() => setPlayerModalVisible(false)}>
                <Icon name="xmark" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            
            <TextInput
              style={[styles.searchInput, {backgroundColor: isDark ? colors.background : '#f5f5f5', color: colors.textPrimary}]}
              placeholder="Cerca giocatore..."
              placeholderTextColor={colors.textSecondary}
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
            
            <ScrollView style={styles.modalPlayerList}>
              <View style={styles.playerGrid}>
                {getAvailablePlayers().map(player => renderPlayerCard(player))}
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyText: {
    fontSize: 16,
    textAlign: 'center',
    marginTop: 16,
  },
  matchHeader: {
    backgroundColor: '#5d2d91', // Slightly different purple than navbar
    padding: 12,
    alignItems: 'center',
  },
  teamRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  teamLogo: {
    width: 24,
    height: 24,
    marginHorizontal: 4,
  },
  teamName: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 13,
    marginHorizontal: 4,
  },
  vsText: {
    color: 'rgba(255,255,255,0.7)',
    marginHorizontal: 6,
  },
  formationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  formationLabel: {
    fontSize: 14,
    marginRight: 12,
  },
  formationDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    minWidth: 100,
  },
  formationText: {
    fontSize: 16,
    fontWeight: '600',
    marginRight: 8,
  },
  formationPicker: {
    marginHorizontal: 16,
    borderRadius: 8,
    overflow: 'hidden',
  },
  formationOption: {
    padding: 12,
    alignItems: 'center',
  },
  formationOptionText: {
    fontSize: 15,
    fontWeight: '500',
  },
  pitchContainer: {
    padding: 16,
  },
  pitch: {
    width: PITCH_WIDTH,
    height: PITCH_HEIGHT,
    backgroundColor: '#2d8a4e',
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
  },
  pitchInner: {
    position: 'absolute',
    top: 8,
    left: 8,
    right: 8,
    bottom: 8,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.4)',
    borderRadius: 4,
  },
  centerLine: {
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: 'rgba(255,255,255,0.4)',
  },
  centerCircle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 60,
    height: 60,
    marginLeft: -30,
    marginTop: -30,
    borderRadius: 30,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.4)',
  },
  penaltyAreaTop: {
    position: 'absolute',
    top: 0,
    left: '20%',
    width: '60%',
    height: '18%',
    borderWidth: 2,
    borderTopWidth: 0,
    borderColor: 'rgba(255,255,255,0.4)',
  },
  penaltyAreaBottom: {
    position: 'absolute',
    bottom: 0,
    left: '20%',
    width: '60%',
    height: '18%',
    borderWidth: 2,
    borderBottomWidth: 0,
    borderColor: 'rgba(255,255,255,0.4)',
  },
  goalAreaTop: {
    position: 'absolute',
    top: 0,
    left: '35%',
    width: '30%',
    height: '8%',
    borderWidth: 2,
    borderTopWidth: 0,
    borderColor: 'rgba(255,255,255,0.4)',
  },
  goalAreaBottom: {
    position: 'absolute',
    bottom: 0,
    left: '35%',
    width: '30%',
    height: '8%',
    borderWidth: 2,
    borderBottomWidth: 0,
    borderColor: 'rgba(255,255,255,0.4)',
  },
  pitchSlot: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptySlot: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.25)',
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.5)',
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
  },
  slotPositionLabel: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 8,
    fontWeight: '700' as const,
    marginTop: 1,
    letterSpacing: 0.5,
  },
  selectedPlayerSlot: {
    alignItems: 'center',
  },
  slotImageContainer: {
    position: 'relative',
  },
  slotPlayerImage: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 2,
    borderColor: '#fff',
  },
  slotPlayerInitial: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#441274',
    borderWidth: 2,
    borderColor: '#fff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  slotInitialText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
  },
  jerseyBadge: {
    position: 'absolute',
    bottom: -4,
    right: -4,
    backgroundColor: '#441274',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
    borderWidth: 1.5,
    borderColor: '#fff',
  },
  jerseyBadgeText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700',
  },
  slotPlayerName: {
    color: '#fff',
    fontSize: 9,
    fontWeight: '600',
    marginTop: 4,
    textShadowColor: 'rgba(0,0,0,0.7)',
    textShadowOffset: {width: 1, height: 1},
    textShadowRadius: 2,
    maxWidth: 60,
    textAlign: 'center',
  },
  submitButton: {
    marginHorizontal: 16,
    marginTop: 16,
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  submitText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  bottomPadding: {
    height: 40,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 16,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  searchInput: {
    marginHorizontal: 16,
    marginBottom: 12,
    padding: 12,
    borderRadius: 8,
    fontSize: 15,
  },
  modalPlayerList: {
    paddingHorizontal: 16,
  },
  playerGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingBottom: 32,
  },
  modalPlayerCard: {
    width: (SCREEN_WIDTH - 64) / 3,
    alignItems: 'center',
    padding: 8,
    marginBottom: 12,
  },
  modalPlayerSelected: {
    backgroundColor: 'rgba(68, 18, 116, 0.1)',
    borderRadius: 12,
  },
  modalPlayerDisabled: {
    opacity: 0.5,
  },
  modalPlayerImage: {
    width: 60,
    height: 60,
    borderRadius: 30,
    borderWidth: 2,
    borderColor: '#e0e0e0',
  },
  modalPlayerImageDisabled: {
    borderColor: '#ccc',
  },
  modalPlayerInitial: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#441274',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalPlayerInitialDisabled: {
    backgroundColor: '#999',
  },
  modalInitialText: {
    color: '#fff',
    fontSize: 24,
    fontWeight: '700',
  },
  modalPlayerName: {
    marginTop: 6,
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  modalPlayerNameDisabled: {
    color: '#999',
  },
  unavailableReason: {
    fontSize: 10,
    color: '#e53935',
    marginTop: 2,
    textAlign: 'center',
  },
  selectedBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#4CAF50',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default FormazioneVotingScreen;
